import { ethers } from 'ethers';
import React, { useEffect, useRef, useState } from 'react';
import { useAccount, useSignMessage } from 'wagmi';
import { CPGABI } from '../../ABI/ABI';
import Loader from '../../components/loader/Loader';
import MilestoneSchedulePreview from '../../components/milestoneDuration/MilestoneSchedulePreview';
import Token from '../../components/tokenList/Token';
import ThemedSelect from '../../components/ui/ThemedSelect';
import { mileStoneDuration } from '../../config/data';
import { CPGAddress, targetChainId, ZERO_ADDRESS } from '../../constant/constant';
import {
    CONTRACT_ADDRESS_WARNING_MSG,
    REQUEST_TRANSACTION_SUCCESS_MSG,
    SELF_PAYMENT_WARNING_MSG,
    TRANSACTION_ERROR_MSG,
    USER_REJECT_TRANSACTION_MSG,
} from '../../constant/toasterMessage';
import {
    convertBigToSmallDigitWithSpecificDecimal,
    fireErrorToast,
    fireSuccessToast,
    handleKeyDown,
    handleWheel,
    isContractAddress,
} from '../../utils/commonFunction';
import { ContractWrite } from '../../utils/contractInstance';
import { persistPaymentRequestRecord } from '../../api/backendClient';
import { extractPaymentRequestIdFromReceipt } from '../../utils/paymentRequestEvents';
import { setPaymentRequestMeta } from '../../utils/paymentRequestNotes';
import { publicClient } from '../../utils/wagmiConfig';
import { paymentRefBytes32 } from '../../utils/cpgDiamondReader';

const SendRequest = ({
    onCancel,
    onSuccess,
    getDirectPaymentRequestData,
    getLockedPaymentRequestData,
}) => {
    const [sendPaymentRequestData, setsendPaymentRequestData] = useState({
        userName: '',
        amount: '',
        description: '',
    });
    const [selectedOption, setSelectedOption] = useState('directPayment');
    const [milestoneCount, setMilestoneCount] = useState('');
    const [milestoneDuration, setMilestoneDuration] = useState(
        mileStoneDuration[0].timestamp,
    );
    const [token, setToken] = useState({});
    const [loading, setLoading] = useState(false);
    const [isRecipientContract, setIsRecipientContract] = useState(false);
    const pendingDirectMetaRef = useRef(null);
    const pendingMilestoneMetaRef = useRef(null);

    const { address: walletAddress, chainId } = useAccount();
    const { signMessageAsync } = useSignMessage();

    const persistRequestMetaFromReceipt = async ({
        transactionHash,
        kind,
        snapshot,
    }) => {
        if (!transactionHash || !walletAddress || !snapshot) return;
        try {
            const receipt = await publicClient.waitForTransactionReceipt({
                hash: transactionHash,
            });
            const requestId = extractPaymentRequestIdFromReceipt(receipt, kind);
            if (requestId == null) return;

            const blockNumber =
                receipt?.blockNumber != null
                    ? receipt.blockNumber.toString()
                    : null;

            setPaymentRequestMeta({
                chainId,
                sender: walletAddress,
                requestId,
                kind,
                description: snapshot.description || '',
                createdAt: Date.now(),
            });

            const apiKind = kind === 'milestone' ? 'milestone' : 'direct';
            const result = await persistPaymentRequestRecord(
                walletAddress,
                {
                    kind: apiKind,
                    requestId,
                    txHash: transactionHash,
                    requester: walletAddress,
                    payer: snapshot.payer,
                    tokenAddress: snapshot.tokenAddress,
                    tokenSymbol: snapshot.tokenSymbol || null,
                    tokenDecimals: snapshot.tokenDecimals ?? null,
                    amount: snapshot.amount,
                    amountWei: snapshot.amountWei,
                    chainId: targetChainId,
                    blockNumber,
                    status: 'pending',
                    milestoneCount: snapshot.milestoneCount ?? null,
                    milestoneInterval: snapshot.milestoneInterval ?? null,
                    meta: snapshot.description
                        ? { description: snapshot.description }
                        : null,
                },
                { signMessageAsync },
            );

            if (!result.ok) {
                console.warn(
                    'Failed to persist payment request to backend:',
                    result.reason,
                );
                fireErrorToast(
                    `Request was created on-chain but was not saved to the server. ${result.reason}`,
                );
            }
        } catch (_e) {
            // List still loads from chain; description may be missing for this row.
        }
    };

    const onWriteError = (error) => {
        if (error) {
            if (
                !error?.details?.includes(
                    'MetaMask Tx Signature: User denied transaction signature',
                )
            ) {
                fireErrorToast(TRANSACTION_ERROR_MSG);
            }
            if (
                error?.details?.includes(
                    'MetaMask Tx Signature: User denied transaction signature',
                )
            ) {
                fireErrorToast(USER_REJECT_TRANSACTION_MSG);
            }
        }
        setLoading(false);
    };

    //********************************************** requestPayment Smart Contract Call *****************************************************************//
    const {
        writeContract: requestPayment,
        writeSuccessResponse: requestPaymentResponse,
        writeErrorResponse: requestPaymentErrorResponse,
        updateArgs: requestPaymentArgs,
        transactionHash: requestPaymentTxHash,
    } = ContractWrite({
        address: CPGAddress,
        abi: CPGABI,
        functionName: 'requestPayment',
        initialArgs: [
            sendPaymentRequestData.userName,
            token?.address ?? ZERO_ADDRESS,
            sendPaymentRequestData.amount ?? 1,
            paymentRefBytes32(sendPaymentRequestData.description),
        ],
        enabled: true,
    });

    useEffect(() => {
        if (!requestPaymentResponse || !requestPaymentTxHash) return;

        const pending = pendingDirectMetaRef.current;
        pendingDirectMetaRef.current = null;

        (async () => {
            setLoading(false);
            fireSuccessToast(REQUEST_TRANSACTION_SUCCESS_MSG);
            await persistRequestMetaFromReceipt({
                transactionHash: requestPaymentTxHash,
                kind: 'direct',
                snapshot: pending,
            });
            setsendPaymentRequestData({ userName: '', amount: '', description: '' });
            setToken({});
            getDirectPaymentRequestData?.();
            onSuccess?.();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestPaymentResponse, requestPaymentTxHash]);

    useEffect(() => {
        if (selectedOption === 'directPayment') {
            if (token.decimals && sendPaymentRequestData.amount) {
                const trackedAmount = ethers.parseUnits(
                    sendPaymentRequestData.amount,
                    token?.decimals,
                );
                requestPaymentArgs([
                    sendPaymentRequestData.userName,
                    token?.address ?? ZERO_ADDRESS,
                    trackedAmount.toString(),
                    paymentRefBytes32(sendPaymentRequestData.description),
                ]);
            }
        }
        // eslint-disable-next-line
    }, [sendPaymentRequestData, token, selectedOption]);

    useEffect(() => {
        if (requestPaymentErrorResponse) {
            onWriteError(requestPaymentErrorResponse);
        }
        // eslint-disable-next-line
    }, [requestPaymentErrorResponse]);

    //**************************************************************** requestLockedPayment Smart Contract Call *******************************************************************//
    const {
        writeContract: requestLockedPayment,
        writeSuccessResponse: requestLockedPaymentResponse,
        writeErrorResponse: requestLockedPaymentErrorResponse,
        updateArgs: requestLockedPaymentArgs,
        transactionHash: requestLockedPaymentTxHash,
    } = ContractWrite({
        address: CPGAddress,
        abi: CPGABI,
        functionName: 'requestLockedPayment',
        initialArgs: [
            sendPaymentRequestData.userName,
            token?.address ?? ZERO_ADDRESS,
            sendPaymentRequestData.amount ?? 1,
            milestoneCount,
            mileStoneDuration,
            paymentRefBytes32(sendPaymentRequestData.description),
        ],
        enabled: true,
    });

    useEffect(() => {
        if (!requestLockedPaymentResponse || !requestLockedPaymentTxHash) return;

        const pending = pendingMilestoneMetaRef.current;
        pendingMilestoneMetaRef.current = null;

        (async () => {
            setLoading(false);
            fireSuccessToast(REQUEST_TRANSACTION_SUCCESS_MSG);
            await persistRequestMetaFromReceipt({
                transactionHash: requestLockedPaymentTxHash,
                kind: 'milestone',
                snapshot: pending,
            });
            setsendPaymentRequestData({ userName: '', amount: '', description: '' });
            setMilestoneCount('');
            setSelectedOption('directPayment');
            setToken({});
            setMilestoneDuration(mileStoneDuration[0].timestamp);
            getLockedPaymentRequestData?.();
            onSuccess?.();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestLockedPaymentResponse, requestLockedPaymentTxHash]);

    useEffect(() => {
        if (selectedOption === 'milestone') {
            if (token.decimals && milestoneCount) {
                const trackedAmount = ethers.parseUnits(
                    sendPaymentRequestData.amount.toString(),
                    token?.decimals,
                );
                requestLockedPaymentArgs([
                    sendPaymentRequestData.userName,
                    token?.address ?? ZERO_ADDRESS,
                    trackedAmount.toString(),
                    milestoneCount,
                    milestoneDuration,
                    paymentRefBytes32(sendPaymentRequestData.description),
                ]);
            }
        }
        // eslint-disable-next-line
    }, [sendPaymentRequestData, token, milestoneDuration, milestoneCount, selectedOption]);

    useEffect(() => {
        if (requestLockedPaymentErrorResponse) {
            onWriteError(requestLockedPaymentErrorResponse);
        }
        // eslint-disable-next-line
    }, [requestLockedPaymentErrorResponse]);

    const handleChangeMilestoneCount = (e) => setMilestoneCount(e.target.value);
    const handleChange = (e) =>
        setsendPaymentRequestData({
            ...sendPaymentRequestData,
            [e.target.name]: e.target.value,
        });
    const handleSelectChange = (event) =>
        setMilestoneDuration(Number(event.target.value));
    const handleSelectToken = (item) => setToken(item);

    useEffect(() => {
        const checkAddress = async () => {
            if (ethers.isAddress(sendPaymentRequestData.userName)) {
                const result = await isContractAddress(sendPaymentRequestData.userName);
                setIsRecipientContract(result);
            } else {
                setIsRecipientContract(false);
            }
        };
        checkAddress();
    }, [sendPaymentRequestData.userName]);

    const handleSubmit = async (e) => {
        try {
            setLoading(true);
            e.preventDefault();
            if (!ethers.isAddress(sendPaymentRequestData.userName)) {
                fireErrorToast('Enter a valid receiver wallet address');
                setLoading(false);
                return;
            }
            if (
                sendPaymentRequestData.userName.toLowerCase() ===
                walletAddress?.toLowerCase()
            ) {
                fireErrorToast(SELF_PAYMENT_WARNING_MSG);
                setLoading(false);
                return;
            }
            const isContract = await isContractAddress(sendPaymentRequestData.userName);
            if (isContract) {
                fireErrorToast(CONTRACT_ADDRESS_WARNING_MSG);
                setLoading(false);
                return;
            }
            const trackedAmount = ethers.parseUnits(
                sendPaymentRequestData.amount.toString(),
                token?.decimals,
            );
            if (selectedOption === 'directPayment') {
                pendingDirectMetaRef.current = {
                    description: (sendPaymentRequestData.description || '').trim(),
                    payer: sendPaymentRequestData.userName,
                    tokenAddress: token.address,
                    tokenSymbol: token.symbol || token.name || null,
                    tokenDecimals: token.decimals ?? null,
                    amount: sendPaymentRequestData.amount,
                    amountWei: trackedAmount.toString(),
                };
                requestPayment({
                    abi: CPGABI,
                    address: CPGAddress,
                    functionName: 'requestPayment',
                    args: [
                        sendPaymentRequestData.userName,
                        token?.address ?? ZERO_ADDRESS,
                        trackedAmount.toString(),
                        paymentRefBytes32(sendPaymentRequestData.description),
                    ],
                });
            } else if (selectedOption === 'milestone') {
                pendingMilestoneMetaRef.current = {
                    description: (sendPaymentRequestData.description || '').trim(),
                    payer: sendPaymentRequestData.userName,
                    tokenAddress: token.address,
                    tokenSymbol: token.symbol || token.name || null,
                    tokenDecimals: token.decimals ?? null,
                    amount: sendPaymentRequestData.amount,
                    amountWei: trackedAmount.toString(),
                    milestoneCount: Number(milestoneCount) || null,
                    milestoneInterval: String(milestoneDuration),
                };
                requestLockedPayment({
                    abi: CPGABI,
                    address: CPGAddress,
                    functionName: 'requestLockedPayment',
                    args: [
                        sendPaymentRequestData.userName,
                        token?.address ?? ZERO_ADDRESS,
                        trackedAmount.toString(),
                        milestoneCount,
                        milestoneDuration,
                        paymentRefBytes32(sendPaymentRequestData.description),
                    ],
                });
            }
        } catch (error) {
            setLoading(false);
        }
    };

    // -------- Validation flags for inline button state --------
    const trimmedReceiver = (sendPaymentRequestData.userName || '').trim();
    const isReceiverFilled = trimmedReceiver.length > 0;
    const isReceiverValidAddress = isReceiverFilled && ethers.isAddress(trimmedReceiver);
    const isSelfRequest =
        isReceiverValidAddress &&
        trimmedReceiver.toLowerCase() === walletAddress?.toLowerCase();
    const hasAmount = !!sendPaymentRequestData.amount && Number(sendPaymentRequestData.amount) > 0;
    const hasToken = !!token?.address;
    const isMilestoneInvalid = selectedOption === 'milestone' && !milestoneCount;

    const canSubmit =
        isReceiverValidAddress &&
        !isSelfRequest &&
        !isRecipientContract &&
        hasAmount &&
        hasToken &&
        !isMilestoneInvalid;

    const submitLabel = (() => {
        if (!isReceiverFilled) return 'Enter receiver wallet address';
        if (!isReceiverValidAddress) return 'Invalid wallet address';
        if (isSelfRequest) return 'Cannot request from your own address';
        if (isRecipientContract) return 'Cannot request from a contract address';
        if (!hasAmount) return 'Enter amount';
        if (!hasToken) return 'Select a token';
        if (isMilestoneInvalid) return 'Enter number of milestones';
        return 'Send Request';
    })();

    // -------- Styling --------
    const labelClass =
        'mb-2 block text-xs font-semibold tracking-wide text-zinc-600 dark:text-zinc-400';
    const inputClass = 'cpg-input block w-full px-4 py-3 text-sm';

    return (
        <div className="glass-card animate-fade-in-up overflow-hidden rounded-2xl border border-zinc-200 shadow-xl dark:border-zinc-800">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4 dark:border-zinc-800/80">
                <div>
                    <h3 className="text-base font-semibold text-zinc-900 dark:text-white">
                        Create Payment Request
                    </h3>
                    <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-500">
                        Send a request and we&apos;ll notify the receiver on-chain.
                    </p>
                </div>
                {onCancel && (
                    <button
                        type="button"
                        onClick={onCancel}
                        className="text-zinc-500 transition-colors hover:text-zinc-800 dark:hover:text-zinc-300"
                        aria-label="Close request form"
                    >
                        <i className="fa-solid fa-xmark text-lg" />
                    </button>
                )}
            </div>

            {/* Body */}
            <div className="p-5 space-y-4">
                {/* Receiver */}
                <div>
                    <label htmlFor="userName" className={labelClass}>
                        Request from Address
                    </label>
                    <input
                        type="text"
                        id="userName"
                        name="userName"
                        placeholder="0x..."
                        value={sendPaymentRequestData.userName}
                        onChange={handleChange}
                        className={`${inputClass} font-mono`}
                        spellCheck={false}
                    />
                    {isReceiverFilled && !isReceiverValidAddress && (
                        <p className="mt-1.5 text-xs text-rose-400">
                            <i className="fa-solid fa-circle-exclamation mr-1" />
                            Please enter a valid wallet address.
                        </p>
                    )}
                    {isSelfRequest && (
                        <p className="mt-1.5 text-xs text-rose-400">
                            <i className="fa-solid fa-circle-exclamation mr-1" />
                            You can&apos;t request a payment from your own wallet.
                        </p>
                    )}
                </div>

                {/* Amount + Token */}
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <label htmlFor="amount" className="text-xs font-semibold tracking-wide text-zinc-600 dark:text-zinc-400">
                                Amount
                            </label>
                            {token.name && (
                                <span className="text-[11px] text-zinc-500">
                                    Balance:{' '}
                                    <span className="font-medium text-zinc-800 dark:text-zinc-300">
                                        {convertBigToSmallDigitWithSpecificDecimal(
                                            token.balance,
                                            token.decimals,
                                        )}
                                    </span>
                                </span>
                            )}
                        </div>
                        <input
                            type="number"
                            id="amount"
                            name="amount"
                            placeholder="0.00"
                            min={0}
                            value={sendPaymentRequestData.amount}
                            onChange={handleChange}
                            onWheel={handleWheel}
                            onKeyDown={handleKeyDown}
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label htmlFor="token" className={labelClass}>
                            Token
                        </label>
                        <Token handleSelectToken={handleSelectToken} token={token} />
                    </div>
                </div>

                {/* Description */}
                <div>
                    <label htmlFor="description" className={labelClass}>
                        Description
                    </label>
                    <textarea
                        id="description"
                        name="description"
                        placeholder="What is this payment for?"
                        value={sendPaymentRequestData.description}
                        onChange={handleChange}
                        className={`${inputClass} min-h-[88px] resize-y`}
                    />
                </div>

                {/* Payment type segmented control */}
                <div>
                    <label className={labelClass}>Payment Type</label>
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            type="button"
                            onClick={() => setSelectedOption('directPayment')}
                            className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-all ${selectedOption === 'directPayment'
                                    ? 'border-teal-300 bg-teal-50 text-teal-900 ring-1 ring-teal-200 dark:border-emerald-500/30 dark:bg-emerald-500/[0.08] dark:text-emerald-300 dark:ring-emerald-500/20'
                                    : 'border-zinc-200 bg-zinc-50 text-zinc-600 hover:border-zinc-300 hover:text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900/50 dark:text-zinc-400 dark:hover:border-zinc-600 dark:hover:text-zinc-200'
                                }`}
                        >
                            Direct Payment
                        </button>
                        <button
                            type="button"
                            onClick={() => setSelectedOption('milestone')}
                            className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-all ${selectedOption === 'milestone'
                                    ? 'border-teal-300 bg-teal-50 text-teal-900 ring-1 ring-teal-200 dark:border-emerald-500/30 dark:bg-emerald-500/[0.08] dark:text-emerald-300 dark:ring-emerald-500/20'
                                    : 'border-zinc-200 bg-zinc-50 text-zinc-600 hover:border-zinc-300 hover:text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900/50 dark:text-zinc-400 dark:hover:border-zinc-600 dark:hover:text-zinc-200'
                                }`}
                        >
                            Milestone Payment
                        </button>
                    </div>
                </div>

                {/* Milestone extras */}
                {selectedOption === 'milestone' && (
                    <div className="grid sm:grid-cols-2 gap-4 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
                        <div>
                            <label className={labelClass}>Milestone Duration</label>
                            <ThemedSelect
                                value={milestoneDuration}
                                onChange={handleSelectChange}
                                options={mileStoneDuration.map((item) => ({
                                    value: item.timestamp,
                                    label: item.name,
                                }))}
                            />
                        </div>
                        <div>
                            <label htmlFor="milestoneCount" className={labelClass}>
                                Number of Milestones
                            </label>
                            <input
                                type="number"
                                id="milestoneCount"
                                placeholder="e.g. 4"
                                min={1}
                                value={milestoneCount}
                                onChange={handleChangeMilestoneCount}
                                onWheel={handleWheel}
                                onKeyDown={handleKeyDown}
                                className={inputClass}
                            />
                        </div>

                        <MilestoneSchedulePreview
                            totalAmount={sendPaymentRequestData.amount}
                            milestoneCount={milestoneCount}
                            intervalSeconds={milestoneDuration}
                            tokenSymbol={token.symbol || token.name || ''}
                            className="mt-4 sm:col-span-2"
                        />
                    </div>
                )}

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    {loading ? (
                        <div className="flex flex-1 items-center justify-center rounded-xl border border-teal-200 bg-teal-50 py-3 dark:border-emerald-500/20 dark:bg-emerald-500/10">
                            <Loader stroke="#0d9488" size="22px" />
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={!canSubmit}
                            className={`inline-flex flex-1 items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition-all ${canSubmit
                                    ? 'bg-teal-600 text-white shadow-md hover:bg-teal-500 dark:bg-gradient-to-r dark:from-emerald-600 dark:to-teal-600 dark:hover:from-emerald-500 dark:hover:to-teal-500 dark:text-white dark:shadow-emerald-900/30'
                                    : 'cursor-not-allowed border border-zinc-200 bg-zinc-200 text-zinc-500 dark:border-zinc-700/60 dark:bg-zinc-800/70 dark:text-zinc-500'
                                }`}
                        >
                            <i className="fa-solid fa-paper-plane text-xs" />
                            {submitLabel}
                        </button>
                    )}
                    {onCancel && (
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={loading}
                            className="rounded-xl border border-zinc-300 bg-zinc-100 px-5 py-3 text-sm font-semibold text-zinc-800 transition-colors hover:bg-zinc-200 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900/50 dark:text-zinc-300 dark:hover:bg-zinc-800/60 dark:hover:text-white sm:w-32"
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SendRequest;
